/**
 * The engine's lifetime, tied to who is signed in.
 *
 * One engine per account, created when a learner signs in and discarded when
 * they sign out. Discarding it does not touch their work: the outbox, cursor and
 * conflicts live in IndexedDB keyed by account, so signing back in — or handing
 * the iPad to a classmate and back — resumes exactly where it stopped.
 *
 * Signed out there is no engine at all. That is the honest arrangement: guest
 * work has nowhere to sync to, and pretending otherwise would mean a queue whose
 * destination does not exist.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from '../auth/AuthProvider';
import { createRawRepositories, observeWrites, repositories } from '../storage/repositories';
import { createBackend, createRawBackend } from '../curriculum/progress';
import { SyncEngine, type SyncSnapshot } from './engine';
import { readLocalRecord } from './records';
import { createSyncStores } from './stores';
import type { SyncStatus } from './types';

export interface SyncContextValue {
  /** Null while signed out — there is nothing to sync to. */
  engine: SyncEngine | null;
  snapshot: SyncSnapshot;
}

const IDLE: SyncSnapshot = {
  status: 'saved-local',
  pending: 0,
  conflicts: [],
  lastSyncedAt: null,
  attention: null,
};

const SyncContext = createContext<SyncContextValue>({ engine: null, snapshot: IDLE });

export function SyncProvider({ children }: { children: ReactNode }) {
  const { state, api, reachable } = useAuth();
  const accountId = state.status === 'signed-in' ? (state.user?.id ?? '') : '';
  const [snapshot, setSnapshot] = useState<SyncSnapshot>(IDLE);
  const engineRef = useRef<SyncEngine | null>(null);

  const engine = useMemo(() => {
    engineRef.current?.stop();
    if (!accountId) {
      engineRef.current = null;
      setSnapshot(IDLE);
      return null;
    }
    const next = new SyncEngine({
      accountId,
      api,
      stores: createSyncStores(accountId),
      // Raw storage: what the engine writes during a pull must not be
      // announced back as though the learner had done it.
      local: { repos: createRawRepositories(), progress: createRawBackend() },
      onChange: setSnapshot,
    });
    engineRef.current = next;
    return next;
  }, [accountId, api]);

  // A first cycle on sign-in, so a learner who was away sees the other iPad's
  // work without having to do anything.
  useEffect(() => {
    if (!engine) return;
    void engine.run();
    return () => engine.stop();
  }, [engine]);

  /**
   * Local writes become queued operations.
   *
   * Registered only while an engine exists, which is only while somebody is
   * signed in — so a guest's writing is never queued for an account it does not
   * belong to. The listener runs after the local save has already committed and
   * cannot delay or fail it.
   */
  useEffect(() => {
    if (!engine) return;
    return observeWrites((write) => {
      void (async () => {
        try {
          const local = await readLocalRecord(
            { repos: repositories(), progress: createBackend() },
            write.kind,
            write.id,
          );
          if (!local && !write.deleted) return;
          await engine.enqueue({
            kind: write.kind,
            recordId: write.id,
            payload: local?.payload ?? {},
            title: local?.title,
            feature: local?.feature,
            deleted: write.deleted ?? local?.deleted ?? false,
          });
        } catch (error) {
          // Never silent. A write that fails to queue is work that will not
          // reach the account, and swallowing it is how that stays invisible.
          console.error('[sync] could not queue a local change', write, error);
        }
      })();
    });
  }, [engine]);

  // Coming back online is the moment queued work can actually move.
  useEffect(() => {
    if (!engine || typeof window === 'undefined') return;
    const onOnline = () => void engine.run();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [engine]);

  // The session came back after expiring: the engine paused rather than failing,
  // so it only needs telling to carry on.
  useEffect(() => {
    if (engine && reachable) engine.resume();
  }, [engine, reachable]);

  const value = useMemo<SyncContextValue>(() => ({ engine, snapshot }), [engine, snapshot]);
  return <SyncContext.Provider value={value}>{children}</SyncContext.Provider>;
}

export function useSync(): SyncContextValue {
  return useContext(SyncContext);
}

/** What each status says to a learner. Every one of these must be true. */
export function describeSyncStatus(status: SyncStatus, pending: number): string {
  switch (status) {
    case 'saved-local':
      return 'Saved on this iPad';
    case 'syncing':
      return pending > 0 ? `Syncing ${pending} change${pending === 1 ? '' : 's'}` : 'Syncing';
    case 'synced':
      return 'Synced';
    case 'offline':
      return 'Offline — saved on this iPad';
    case 'needs-attention':
      return 'Needs attention';
  }
}
